import React from 'react';
import Icon from './Icon';

const CALENDLY = 'https://calendly.com/salesyo-ai/30min';
const SUBSCRIBE = 'https://instagrambot.caretalent.fr/billing/plans';

const plans = [
  {
    cls: 'plan',
    name: 'Starter',
    price: <><span className="amt">$297</span><span className="per">/ mo</span></>,
    conv: <><b>500</b> conversations / mo</>,
    desc: 'For coaches & founders losing inbound leads to slow replies.',
    features: [
      { t: 'ck', c: <>AI qualifies & <b>books calls</b></> },
      { t: 'ck', c: <><b>Instagram, Messenger & WhatsApp</b></> },
      { t: 'ck', c: 'Text follow-ups' },
      { t: 'ck', c: 'Human handoff (Slack / email)' },
      { t: 'ck', c: '1 connected account' }
    ],
    btnCls: 'btn btn-ghost',
    btnLabel: 'Start 14-day trial'
  },
  {
    cls: 'plan feature',
    flag: 'Most popular',
    name: 'Growth',
    price: <><span className="amt">$597</span><span className="per">/ mo</span></>,
    conv: <><b>1,500</b> conversations / mo</>,
    desc: 'For creators scaling across DMs, email and their website.',
    features: [
      { t: 'plus', c: 'Everything in Starter' },
      { t: 'ck', c: <><b>Email</b> — Gmail & Outlook</> },
      { t: 'ck', c: <><b>AI web widget</b> on your site</> },
      { t: 'ck', c: <>Up to <b>3</b> brands</> },
      { t: 'ck', c: 'Advanced analytics' }
    ],
    btnCls: 'btn btn-wa',
    btnLabel: 'Start 14-day trial'
  },
  {
    cls: 'plan',
    name: 'Pro',
    price: <><span className="amt">$1,197</span><span className="per">/ mo</span></>,
    conv: <><b>3,000</b> conversations / mo</>,
    desc: 'For agencies & high-volume operators wanting every channel.',
    features: [
      { t: 'plus', c: 'Everything in Growth' },
      { t: 'ck', c: <><b>All channels</b> — LinkedIn, X, Telegram</> },
      { t: 'ck', c: <><b>Audio</b> voice-note follow-ups</> },
      { t: 'ck', c: <><b>Premium AI reasoning</b> on hot leads</> },
      { t: 'ck', c: <><b>Unlimited</b> brands + priority support</> }
    ],
    btnCls: 'btn btn-black',
    btnLabel: 'Start 14-day trial'
  },
  {
    cls: 'plan dark',
    name: 'Enterprise',
    price: <span className="cus">Custom</span>,
    conv: <>Beyond <b>3,000</b> conversations</>,
    desc: 'For teams past self-serve volume, or a scope to shape together.',
    features: [
      { t: 'ck', c: 'Custom volume & pricing' },
      { t: 'ck', c: 'Dedicated onboarding' },
      { t: 'ck', c: 'Priority routing & SLA' },
      { t: 'ck', c: 'Solutions engineer' }
    ],
    btnCls: 'btn btn-wa',
    btnLabel: 'Book a call'
  }
];

const matrix = [
  { grp: 'Volume' },
  { feat: 'Conversations / month', vals: ['500', '1,500', '3,000', 'Custom'], ct: true },
  { feat: 'Connected accounts / brands', vals: ['1', '3', 'Unlimited', 'Unlimited'], ct: true },
  { feat: 'Auto top-up (never run out mid-chat)', vals: [true, true, true, true] },
  { grp: 'Channels' },
  { feat: 'Instagram, Messenger & WhatsApp', vals: [true, true, true, true] },
  { feat: 'Email — Gmail & Outlook', vals: [false, true, true, true] },
  { feat: 'AI web widget on your site', vals: [false, true, true, true] },
  { feat: 'LinkedIn, X & Telegram', vals: [false, false, true, true] },
  { grp: 'The AI operator' },
  { feat: 'Qualifies & books calls', vals: [true, true, true, true] },
  { feat: 'Text follow-ups', vals: [true, true, true, true] },
  { feat: 'Audio voice-note follow-ups', vals: [false, false, true, true] },
  { feat: 'Premium AI reasoning on hot leads', vals: [false, false, true, true] },
  { feat: 'Human handoff (Slack / email)', vals: [true, true, true, true] },
  { grp: 'Insight & support' },
  { feat: 'Analytics', vals: ['Basic', 'Advanced', 'Advanced', 'Advanced'], ct: true },
  { feat: 'Support', vals: ['Chat', 'Chat', 'Priority', 'Dedicated'], ct: true }
];

const topups = [
  { k: '+100 conversations', v: '$49' },
  { k: '+500 conversations', v: '$221' },
  { k: '+1,000 conversations', v: '$402' },
  { k: '+5,000 conversations', v: '$1,838' }
];

function Cell({ val, ct }) {
  if (val === true) return <td className="yes"><Icon name="check" /></td>;
  if (val === false) return <td className="no">—</td>;
  return <td className={ct ? 'ct' : ''}>{val}</td>;
}

function Pricing() {
  return (
    <section id="pricing" style={{ paddingTop: 0 }}>
      <div className="wrap">
        <div className="sec-head">
          <div className="eyebrow">Pricing</div>
          <h2>A fraction of a human setter</h2>
          <p>A dedicated human setter costs $2,000–$4,000/month. Salesyo starts at $297, works every channel 24/7, and never quits. Simple monthly plans by volume, top up anytime.</p>
        </div>

        <div className="plans">
          {plans.map((p) => (
            <div className={p.cls} key={p.name}>
              {p.flag && <div className="flag">{p.flag}</div>}
              <div className="pname">{p.name}</div>
              <div className="pr">{p.price}</div>
              <div className="conv">{p.conv}</div>
              <div className="pdesc">{p.desc}</div>
              <ul className="pf">
                {p.features.map((f, i) => (
                  <li key={i}><span className={f.t}><Icon name={f.t === 'plus' ? 'plus' : 'check'} /></span><span>{f.c}</span></li>
                ))}
              </ul>
              <a className={p.btnCls} href={p.btnLabel === 'Book a call' ? CALENDLY : SUBSCRIBE} target="_blank" rel="noopener noreferrer" style={{ width: '100%' }}>{p.btnLabel}</a>
            </div>
          ))}
        </div>

        <div className="mtable-wrap">
          <table>
            <thead>
              <tr>
                <th className="feat">Feature</th>
                <th>Starter<span className="pt">$297</span></th>
                <th className="pop">Growth<span className="pt">$597</span></th>
                <th>Pro<span className="pt">$1,197</span></th>
                <th>Enterprise<span className="pt">Custom</span></th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row, i) =>
                row.grp ? (
                  <tr className="grp" key={i}><td colSpan={5}>{row.grp}</td></tr>
                ) : (
                  <tr key={i}>
                    <td className="feat">{row.feat}</td>
                    {row.vals.map((v, j) => <Cell key={j} val={v} ct={row.ct} />)}
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        <div className="recharge">
          <div>
            <div className="eyebrow" style={{ color: 'var(--wa)' }}>Never lose a hot lead</div>
            <h3>Out of conversations? Your AI stays live.</h3>
            <p>When you hit your plan, <span className="green">auto top-up</span> keeps the operator running so it never goes silent mid-booking. You set the monthly cap, no surprises. The more you top up, the less each conversation costs.</p>
          </div>
          <div className="rl">
            {topups.map((t) => (
              <div className="rr" key={t.k}><span className="k">{t.k}</span><span className="v">{t.v}</span></div>
            ))}
            <div className="cap">Auto top-up is optional and capped by you. Purchased conversations don't expire.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Pricing;
